import { useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import type { TableProps } from 'antd';
import { DeleteOutlined, EditOutlined, FileTextOutlined, PlusOutlined, TableOutlined } from '@ant-design/icons';
import { ScanText } from 'lucide-react';

import { Bento, DataTable, MenuThaoTac, PageHeader, Tile, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useRoutePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { HopBang, HopCot } from '../components/HopBangCot';
import { HopTruong } from '../components/HopTruong';
import { KhoiThongTinChung } from '../components/KhoiThongTinChung';
import { BADGE_DANG_DUNG, NHAN_COT_LOC, NHAN_KIEU_DU_LIEU, NHAN_LOAI, NHAN_VI_TRI } from '../constants';
import { useChiTietLoaiChungTu, useGhiLoaiChungTu } from '../hooks/useLoaiChungTu';
import type { OcrColumn, OcrField, OcrTable } from '../types';

type Hop =
  | { loai: 'truong'; truong: OcrField | null }
  | { loai: 'bang'; bang: OcrTable | null }
  | { loai: 'cot'; bang: OcrTable; cot: OcrColumn | null };

interface CanXoa {
  tieuDe: string;
  than: string;
  nhan: string;
  lam: () => Promise<unknown>;
}

/** Ô "Có" / "—" cho cờ boolean trong bảng: chữ, không chỉ màu. */
function Co({ co, nhan }: { co: boolean; nhan: string }) {
  return co ? <Badge variant="neutral">{nhan}</Badge> : <span className="text-xs text-content-muted">—</span>;
}

/**
 * Cấu hình một loại chứng từ (`/xu-ly/cau-hinh-ocr/:schemaId`): thông tin chung, trường dữ liệu, bảng và
 * cột (`GET /schemas/{id}` cùng các endpoint con của đợt 2). Mọi thay đổi ghi ngay từng phần;
 * không có quyền sửa (`OCR_CONFIG.UPDATE`) thì chỉ xem, các nút thêm/sửa/xoá ẩn.
 */
export function ChiTietLoaiChungTuView() {
  const { schemaId } = useParams<{ schemaId: string }>();
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const di = useDieuHuong();
  const q = useChiTietLoaiChungTu(schemaId);
  const ghi = useGhiLoaiChungTu();
  const coSua = useRoutePermission('UPDATE');
  const [hop, setHop] = useState<Hop | null>(null);
  const [canXoa, setCanXoa] = useState<CanXoa | null>(null);
  const [xoa, setXoa] = useState<{ dangChay: boolean; loi: string | null }>({ dangChay: false, loi: null });
  const tieuDeDanhSach = t('nav.muc.thietLapChungTuOcr');
  const veDanhSach = () => di('/xu-ly/cau-hinh-ocr');
  const s = q.data;
  const id = schemaId ?? '';

  const moXoa = (x: CanXoa) => {
    setXoa({ dangChay: false, loi: null });
    setCanXoa(x);
  };
  const chayXoa = async () => {
    if (!canXoa) return;
    setXoa({ dangChay: true, loi: null });
    try {
      await canXoa.lam();
      setCanXoa(null);
      setXoa({ dangChay: false, loi: null });
    } catch (err) {
      setXoa({ dangChay: false, loi: dichLoi(err) });
    }
  };

  const vo = (noiDung: ReactNode) => (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.xuLyTaiLieu') },
            { label: tieuDeDanhSach, icon: <ScanText size={12} />, onClick: veDanhSach },
            ...(s ? [{ label: s.name }] : []),
          ]}
          titleIcon={<ScanText size={18} className="text-primary-600" />}
          title={
            s ? (
              <span className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="truncate">{s.name}</span>
                <span className="shrink-0 rounded bg-subtle px-2 py-0.5 font-mono text-xs font-normal text-content-secondary">
                  {s.code}
                </span>
                <Badge variant="neutral">{t(NHAN_LOAI[s.type])}</Badge>
                <Badge variant={BADGE_DANG_DUNG[s.is_active ? 'bat' : 'tat'].variant}>
                  {t(BADGE_DANG_DUNG[s.is_active ? 'bat' : 'tat'].khoa)}
                </Badge>
              </span>
            ) : (
              t('cauHinhOcr.chiTietTieuDe')
            )
          }
          subtitle={s ? t('cauHinhOcr.chiTietMo') : undefined}
        />
        {noiDung}
      </div>
    </div>
  );

  if (q.isLoading) {
    return vo(
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>,
    );
  }
  if (q.error && !s) {
    if (q.error.status === 404) {
      return vo(
        <div className="rounded-lg border border-default bg-surface shadow-sm">
          <Empty
            icon={FileTextOutlined}
            title={t('cauHinhOcr.khongThay')}
            description={t('cauHinhOcr.khongThayMo')}
            action={
              <Button variant="primary" size="md" onClick={veDanhSach}>
                {t('cauHinhOcr.veDanhSach')}
              </Button>
            }
          />
        </div>,
      );
    }
    return vo(
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
        <span className="min-w-0 text-sm text-danger-700">{t('cauHinhOcr.loiChiTiet', { loi: dichLoi(q.error) })}</span>
        <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
          {t('chung.thuLai')}
        </Button>
      </div>,
    );
  }
  if (!s) return null;

  const cotTruong: TableProps<OcrField>['columns'] = [
    {
      title: t('cauHinhOcr.cotTruong'),
      width: 240,
      render: (_v, f) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm text-content-primary">{f.label}</span>
          <span className="truncate font-mono text-2xs text-content-muted">{f.field_key}</span>
        </div>
      ),
    },
    {
      title: t('cauHinhOcr.cotKieu'),
      width: 130,
      render: (_v, f) => <span className="text-xs text-content-secondary">{t(NHAN_KIEU_DU_LIEU[f.data_type])}</span>,
    },
    {
      title: t('cauHinhOcr.cotViTri'),
      width: 140,
      render: (_v, f) => <span className="text-xs text-content-secondary">{t(NHAN_VI_TRI[f.position])}</span>,
    },
    {
      title: t('cauHinhOcr.cotRangBuoc'),
      width: 170,
      render: (_v, f) => (
        <span className="flex flex-wrap gap-1">
          <Co co={f.is_required} nhan={t('cauHinhOcr.batBuoc')} />
          {f.is_unique && <Badge variant="neutral">{t('cauHinhOcr.duyNhat')}</Badge>}
        </span>
      ),
    },
    {
      title: t('cauHinhOcr.cotCotLoc'),
      width: 170,
      render: (_v, f) =>
        f.denorm_target ? (
          <span className="text-xs text-content-secondary">{t(NHAN_COT_LOC[f.denorm_target])}</span>
        ) : (
          <span className="text-xs text-content-muted">—</span>
        ),
    },
    {
      title: t('cauHinhOcr.cotNhanIn'),
      width: 200,
      render: (_v, f) =>
        f.source_label ? (
          <span className="block max-w-[200px] truncate text-xs text-content-secondary" title={f.source_label}>
            {f.source_label}
          </span>
        ) : (
          <span className="text-xs text-content-muted">—</span>
        ),
    },
    ...(coSua
      ? [
          {
            title: t('chung.thaoTac'),
            width: 72,
            align: 'center' as const,
            render: (_v: unknown, f: OcrField) => (
              <MenuThaoTac
                tenBanGhi={f.label}
                muc={[
                  {
                    khoa: 'sua',
                    nhan: t('cauHinhOcr.suaTruong'),
                    icon: <EditOutlined />,
                    onChon: () => setHop({ loai: 'truong', truong: f }),
                  },
                  {
                    khoa: 'xoa',
                    nhan: t('cauHinhOcr.xoaTruong'),
                    icon: <DeleteOutlined />,
                    nhom: 'pha-huy',
                    onChon: () =>
                      moXoa({
                        tieuDe: t('cauHinhOcr.xoaTruongHoi', { ten: f.label }),
                        than: t('cauHinhOcr.xoaTruongThan'),
                        nhan: t('cauHinhOcr.xoaTruong'),
                        lam: () => ghi.xoaTruong.mutateAsync({ id, fieldId: f.id }),
                      }),
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  const cotCuaBang = (b: OcrTable): TableProps<OcrColumn>['columns'] => [
    {
      title: t('chung.stt'),
      width: 56,
      render: (_v, _c, i) => <span className="text-xs tabular-nums text-content-muted">{i + 1}</span>,
    },
    {
      title: t('cauHinhOcr.cotCot'),
      width: 240,
      render: (_v, c) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm text-content-primary">{c.label}</span>
          <span className="truncate font-mono text-2xs text-content-muted">{c.column_key}</span>
        </div>
      ),
    },
    {
      title: t('cauHinhOcr.cotKieu'),
      width: 130,
      render: (_v, c) => <span className="text-xs text-content-secondary">{t(NHAN_KIEU_DU_LIEU[c.data_type])}</span>,
    },
    {
      title: t('cauHinhOcr.cotRangBuoc'),
      width: 120,
      render: (_v, c) => <Co co={c.is_required} nhan={t('cauHinhOcr.batBuoc')} />,
    },
    {
      title: t('cauHinhOcr.cotNhanIn'),
      render: (_v, c) =>
        c.source_label ? (
          <span className="text-xs text-content-secondary">{c.source_label}</span>
        ) : (
          <span className="text-xs text-content-muted">—</span>
        ),
    },
    ...(coSua
      ? [
          {
            title: t('chung.thaoTac'),
            width: 72,
            align: 'center' as const,
            render: (_v: unknown, c: OcrColumn) => (
              <MenuThaoTac
                tenBanGhi={c.label}
                muc={[
                  {
                    khoa: 'sua',
                    nhan: t('cauHinhOcr.suaCot'),
                    icon: <EditOutlined />,
                    onChon: () => setHop({ loai: 'cot', bang: b, cot: c }),
                  },
                  {
                    khoa: 'xoa',
                    nhan: t('cauHinhOcr.xoaCot'),
                    icon: <DeleteOutlined />,
                    nhom: 'pha-huy',
                    onChon: () =>
                      moXoa({
                        tieuDe: t('cauHinhOcr.xoaCotHoi', { ten: c.label }),
                        than: t('cauHinhOcr.xoaCotThan'),
                        nhan: t('cauHinhOcr.xoaCot'),
                        lam: () => ghi.xoaCot.mutateAsync({ id, tableId: b.id, columnId: c.id }),
                      }),
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  return vo(
    <>
      <Bento>
        <KhoiThongTinChung
          key={s.updated_at}
          schema={s}
          coSua={coSua}
          dangLuu={ghi.sua.isPending}
          onLuu={(du) => ghi.sua.mutateAsync({ id, du })}
        />

        <Tile span="col-span-12">
          <TileHeader
            icon={<FileTextOutlined />}
            tone="primary"
            title={t('cauHinhOcr.khoiTruong')}
            mo={t('cauHinhOcr.khoiTruongMo', { so: soVN(s.fields.length, locale) })}
            action={
              coSua ? (
                <Button variant="outline" size="sm" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'truong', truong: null })}>
                  {t('cauHinhOcr.themTruong')}
                </Button>
              ) : undefined
            }
          />
          <DataTable<OcrField>
            caption={t('cauHinhOcr.bangTruongCaption', { ten: s.name })}
            rowKey="id"
            dataSource={s.fields}
            columns={cotTruong}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  icon={FileTextOutlined}
                  title={t('cauHinhOcr.chuaCoTruong')}
                  description={t('cauHinhOcr.chuaCoTruongMo')}
                  action={
                    coSua ? (
                      <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'truong', truong: null })}>
                        {t('cauHinhOcr.themTruongDauTien')}
                      </Button>
                    ) : undefined
                  }
                />
              ),
            }}
          />
        </Tile>

        <Tile span="col-span-12">
          <TileHeader
            icon={<TableOutlined />}
            tone="primary"
            title={t('cauHinhOcr.khoiBang')}
            mo={t('cauHinhOcr.khoiBangMo', { so: soVN(s.tables.length, locale) })}
            action={
              coSua ? (
                <Button variant="outline" size="sm" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'bang', bang: null })}>
                  {t('cauHinhOcr.themBang')}
                </Button>
              ) : undefined
            }
          />
          {s.tables.length === 0 ? (
            <Empty
              icon={TableOutlined}
              title={t('cauHinhOcr.chuaCoBang')}
              description={t('cauHinhOcr.chuaCoBangMo')}
              action={
                coSua ? (
                  <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'bang', bang: null })}>
                    {t('cauHinhOcr.themBangDauTien')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="space-y-4 p-4">
              {s.tables.map((b) => (
                <section key={b.id} aria-label={b.name} className="overflow-hidden rounded-lg border border-default">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-default bg-subtle px-4 py-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-content-primary">{b.name}</h3>
                      <p className="mt-0.5 flex flex-wrap items-center gap-2 text-2xs text-content-muted">
                        <span className="font-mono">{b.table_key}</span>
                        <span>· {t(b.has_header_row ? 'cauHinhOcr.coHangTieuDe' : 'cauHinhOcr.khongHangTieuDe')}</span>
                        <span>· {t('cauHinhOcr.soCot', { so: soVN(b.columns.length, locale) })}</span>
                      </p>
                      {b.description && <p className="mt-1 text-xs text-content-secondary">{b.description}</p>}
                    </div>
                    {coSua && (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button variant="ghost" size="sm" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'cot', bang: b, cot: null })}>
                          {t('cauHinhOcr.themCot')}
                        </Button>
                        <MenuThaoTac
                          tenBanGhi={b.name}
                          muc={[
                            {
                              khoa: 'sua',
                              nhan: t('cauHinhOcr.suaBang'),
                              icon: <EditOutlined />,
                              onChon: () => setHop({ loai: 'bang', bang: b }),
                            },
                            {
                              khoa: 'xoa',
                              nhan: t('cauHinhOcr.xoaBang'),
                              icon: <DeleteOutlined />,
                              nhom: 'pha-huy',
                              onChon: () =>
                                moXoa({
                                  tieuDe: t('cauHinhOcr.xoaBangHoi', { ten: b.name }),
                                  than: t('cauHinhOcr.xoaBangThan', { so: soVN(b.columns.length, locale) }),
                                  nhan: t('cauHinhOcr.xoaBang'),
                                  lam: () => ghi.xoaBang.mutateAsync({ id, tableId: b.id }),
                                }),
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                  <DataTable<OcrColumn>
                    caption={t('cauHinhOcr.bangCotCaption', { ten: b.name })}
                    rowKey="id"
                    dataSource={b.columns}
                    columns={cotCuaBang(b)}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    locale={{
                      emptyText: <Empty icon={TableOutlined} title={t('cauHinhOcr.chuaCoCot')} description={t('cauHinhOcr.chuaCoCotMo')} />,
                    }}
                  />
                </section>
              ))}
            </div>
          )}
        </Tile>
      </Bento>

      {hop?.loai === 'truong' && (
        <HopTruong
          open
          truong={hop.truong}
          tatCa={s.fields}
          dangGui={ghi.themTruong.isPending || ghi.suaTruong.isPending}
          onClose={() => setHop(null)}
          onLuu={async (du) => {
            if (hop.truong) await ghi.suaTruong.mutateAsync({ id, fieldId: hop.truong.id, du });
            else
              await ghi.themTruong.mutateAsync({
                id,
                du: { ...du, display_order: s.fields.length } as Parameters<typeof ghi.themTruong.mutateAsync>[0]['du'],
              });
            setHop(null);
          }}
        />
      )}
      {hop?.loai === 'bang' && (
        <HopBang
          open
          bang={hop.bang}
          dangGui={ghi.themBang.isPending || ghi.suaBang.isPending}
          onClose={() => setHop(null)}
          onLuu={async (du) => {
            if (hop.bang) await ghi.suaBang.mutateAsync({ id, tableId: hop.bang.id, du });
            else await ghi.themBang.mutateAsync({ id, du: du as Parameters<typeof ghi.themBang.mutateAsync>[0]['du'] });
            setHop(null);
          }}
        />
      )}
      {hop?.loai === 'cot' && (
        <HopCot
          open
          cot={hop.cot}
          tenBang={hop.bang.name}
          dangGui={ghi.themCot.isPending || ghi.suaCot.isPending}
          onClose={() => setHop(null)}
          onLuu={async (du) => {
            if (hop.cot) await ghi.suaCot.mutateAsync({ id, tableId: hop.bang.id, columnId: hop.cot.id, du });
            else
              await ghi.themCot.mutateAsync({
                id,
                tableId: hop.bang.id,
                du: { ...du, display_order: hop.bang.columns.length } as Parameters<typeof ghi.themCot.mutateAsync>[0]['du'],
              });
            setHop(null);
          }}
        />
      )}
      <HopXacNhanThaoTac
        open={canXoa !== null}
        tieuDe={canXoa?.tieuDe ?? ''}
        than={canXoa?.than ?? ''}
        nhanXacNhan={canXoa?.nhan ?? ''}
        nguyHiem
        dangChay={xoa.dangChay}
        loi={xoa.loi}
        onClose={() => setCanXoa(null)}
        onXacNhan={() => void chayXoa()}
      />
    </>,
  );
}
